import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { usePostDetails } from '~/post/hooks/usePostDetails';
import { PostEntity } from '~/post/PostEntity';
import { purple } from '@bufferapp/ui/style/colors';
import Button from '@bufferapp/ui/Button';
import { Flash } from '@bufferapp/ui/Icon';
import { useAccount } from '~/accountContext';
import { Services } from '~/constants';
import PostTag from '../PostTag';
import PostFooter from '../PostFooter';
import PostStats from '../PostStats';
import RenderPostMetaBar from './RenderPostMetaBar';
import PostErrorBanner from '../PostErrorBanner';
import PostPendingBanner from '../PostPendingBanner';
import CardHeader from '../CardHeader';
import Card from '../Card';
import UpdateContent from '../UpdateContent';

const PostContainer = styled.div`
  display: flex;
  width: 100%;
`;

const PostSubContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
`;

const PostContent = styled.div`
  padding: 1rem;
  cursor: ${(props) => (props.draggable ? 'grab' : 'inherit')};
  opacity: ${(props) => (props.dragging ? 0 : 1)};
`;

const StatsContainer = styled.div`
  position: relative;
`;

const UpgradeCtaStyled = styled.div`
  position: absolute;
  display: flex;
  width: 490px;
  right: 16px;
  bottom: 6px;
  align-items: center;
  justify-content: center;

  button {
    font-weight: 600;
    height: 28px;
    font-size: 12px;

    svg {
      color: ${purple};
    }
  }
`;

const UpgradeCta = () => {
  const { account } = useAccount();
  const [canStartTrial, setCanStartTrial] = useState(false);
  const [isFreeUser, setIsFreeUser] = useState(false);
  useEffect(() => {
    if (account?.currentOrganization?.billing) {
      const isFree =
        account?.currentOrganization?.billing?.subscription?.plan?.id ===
        'free';
      setCanStartTrial(account?.currentOrganization?.billing?.canStartTrial);
      setIsFreeUser(isFree);
    }
  }, [account.currentOrganization.billing]);

  if (isFreeUser) {
    return (
      <UpgradeCtaStyled>
        <Button
          fullWidth
          type="secondary"
          icon={<Flash />}
          label={
            canStartTrial
              ? 'Start a trial to unlock your insights'
              : 'Upgrade to unlock your insights'
          }
          onClick={() => {
            const { actions, MODALS } = window?.appshell || {};
            actions.openModal(
              canStartTrial ? MODALS.startTrial : MODALS.planSelector,
              {
                cta: canStartTrial
                  ? 'sentPosts-advMetrics-startTrial-1'
                  : 'sentPosts-advMetrics-upgrade-1',
                ctaButton: canStartTrial
                  ? 'sentPosts-advMetrics-startTrial-1'
                  : 'sentPosts-advMetrics-upgrade-1',
                upgradePathName: canStartTrial
                  ? 'sentPosts-trial'
                  : 'sentPosts-upgrade',
              }
            );
          }}
        />
      </UpgradeCtaStyled>
    );
  }

  return null;
};

const Post = ({
  isConfirmingDelete,
  isDeleting,
  isWorking,
  onDeleteConfirmClick,
  onEditClick,
  onShareNowClick,
  onShareAgainClick,
  onShareLinkClick,
  onRequeueClick,
  locationName,
  sourceUrl,
  subprofileID,
  subprofiles,
  draggable,
  dragging,
  isOver,
  statistics,
  profileService,
  profileServiceType,
  serviceLink,
  isManager,
  day,
  dueTime,
  sharedBy,
  basic,
  commentEnabled,
  commentText,
  hasCommentEnabled,
  hasFirstCommentFlip,
  hasPushNotifications,
  onSetRemindersClick,
  hasUserTags,
  campaignDetails,
  onCampaignTagClick,
  hasCampaignsFeature,
  postContent,
  annotations,
  shouldShowEditButton,
  hasAnalyticsOnPosts,
  hasTwitterImpressions,
  channelData,
  isCampaign,
  post,
}) => {
  const isSent = PostEntity.isSent(post);
  const isPastReminder = PostEntity.isPastReminder(post);
  const { postAction, ...headerDetails } = usePostDetails(post);
  const hasError = PostEntity.hasError(post);
  const hasReminderError =
    !isSent &&
    !hasError &&
    !hasPushNotifications &&
    PostEntity.isInstagramReminder(post) &&
    !isPastReminder;
  const showPendingTiktokBanner =
    isCampaign && !hasError && PostEntity.isPendingTiktok(post);
  const cardDetails = {
    faded: isDeleting,
    dragging,
    isOver,
  };

  // StartPage is our only native service at the moment.
  // It does not require an paid plan for unlocking the post insights. They are available in all plans.
  const isNativeService = profileService === 'startPage';
  hasAnalyticsOnPosts = isNativeService || hasAnalyticsOnPosts;

  return (
    <PostContainer data-cy="post" data-testid="post">
      <PostSubContainer>
        <Card state={cardDetails}>
          {hasReminderError && (
            <PostErrorBanner
              dragging={dragging}
              errorLabel="Set Up Reminders"
              error="Shoot, looks like we can't publish this until you set up Reminders. Want to set them up now?"
              errorAction={onSetRemindersClick}
            />
          )}
          {showPendingTiktokBanner && (
            <PostPendingBanner
              serviceName={Services.get(profileService).formattedName}
            />
          )}
          {hasError && (
            <PostErrorBanner
              dragging={dragging}
              error={PostEntity.getError(post)}
              errorLabel={PostEntity.getErrorLabel(post)}
              errorLink={PostEntity.getErrorLink(post)}
            />
          )}
          {headerDetails && <CardHeader headerDetails={headerDetails} />}
          <PostContent draggable={draggable} dragging={dragging}>
            <UpdateContent
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...postContent}
              profileService={profileService}
              basic={basic}
              channelData={channelData}
              annotations={annotations}
            />
          </PostContent>

          {hasCampaignsFeature && campaignDetails && (
            <PostTag
              name={campaignDetails.name}
              color={campaignDetails.color}
              dragging={dragging}
              onPostTagClick={onCampaignTagClick}
            />
          )}

          <RenderPostMetaBar
            profileService={profileService}
            dragging={dragging}
            locationName={locationName}
            sourceUrl={sourceUrl}
            subprofileID={subprofileID}
            subprofiles={subprofiles}
            isSent={isSent}
            isPastReminder={isPastReminder}
          />
          {isSent &&
            !PostEntity.isRetweet(post) &&
            PostEntity.getChannelName(post) !== 'googlebusiness' &&
            PostEntity.getChannelName(post) !== 'tiktok' &&
            PostEntity.getChannelName(post) !== 'mastodon' && (
              <StatsContainer>
                {!hasAnalyticsOnPosts && !isNativeService && <UpgradeCta />}
                <PostStats
                  hasAnalyticsOnPosts={hasAnalyticsOnPosts}
                  showTwitterMentions={hasTwitterImpressions}
                  statistics={statistics}
                  profileService={profileService}
                  profileServiceType={profileServiceType}
                  profileId={PostEntity.getChannelId(post)}
                />
              </StatsContainer>
            )}
          <PostFooter
            post={post}
            postId={PostEntity.getId(post)}
            profileId={PostEntity.getChannelId(post)}
            isManager={isManager}
            isDeleting={isDeleting}
            isConfirmingDelete={isConfirmingDelete}
            isWorking={isWorking}
            onDeleteConfirmClick={onDeleteConfirmClick}
            onEditClick={onEditClick}
            onShareAgainClick={onShareAgainClick}
            onShareLinkClick={onShareLinkClick}
            onShareNowClick={onShareNowClick}
            postAction={postAction}
            dragging={dragging}
            onRequeueClick={onRequeueClick}
            threadCounter={Array.isArray(post.thread) ? post.thread.length : 0}
            serviceLink={serviceLink}
            isSent={isSent}
            isPastReminder={isPastReminder}
            day={day}
            dueTime={dueTime}
            sharedBy={sharedBy}
            commentEnabled={commentEnabled}
            commentText={commentText}
            hasCommentEnabled={hasCommentEnabled}
            hasFirstCommentFlip={hasFirstCommentFlip}
            hasUserTags={hasUserTags}
            shouldShowEditButton={shouldShowEditButton}
          />
        </Card>
      </PostSubContainer>
    </PostContainer>
  );
};

Post.propTypes = {
  isConfirmingDelete: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isWorking: PropTypes.bool,
  onRequeueClick: PropTypes.func,
  onDeleteConfirmClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onShareNowClick: PropTypes.func,
  onShareAgainClick: PropTypes.func,
  onShareLinkClick: PropTypes.func,
  draggable: PropTypes.bool,
  dragging: PropTypes.bool,
  isOver: PropTypes.bool,
  onDropPost: PropTypes.func,
  serviceLink: PropTypes.string,
  isManager: PropTypes.bool,
  day: PropTypes.string,
  dueTime: PropTypes.string,
  sharedBy: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  post: PropTypes.shape({
    id: PropTypes.string,
    thread: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        media: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        extra_media: PropTypes.arrayOf(
          PropTypes.oneOfType([PropTypes.object, PropTypes.string])
        ),
      })
    ),
  }).isRequired,
  basic: PropTypes.bool,
  shouldShowEditButton: PropTypes.bool,
  hasAnalyticsOnPosts: PropTypes.bool,
  hasTwitterImpressions: PropTypes.bool,
  channelData: PropTypes.shape({
    share_to_feed: PropTypes.string,
  }),
  isCampaign: PropTypes.bool,
  locationName: PropTypes.string,
  sourceUrl: PropTypes.string,
  subprofileID: PropTypes.string,
  subprofiles: PropTypes.arrayOf(PropTypes.shape({})),
  statistics: PropTypes.arrayOf(PropTypes.shape({})),
  profileService: PropTypes.string,
  profileServiceType: PropTypes.string,
  commentEnabled: PropTypes.bool,
  commentText: PropTypes.string,
  hasCommentEnabled: PropTypes.bool,
  hasFirstCommentFlip: PropTypes.bool,
  hasPushNotifications: PropTypes.bool,
  onSetRemindersClick: PropTypes.func,
  hasUserTags: PropTypes.bool,
  campaignDetails: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.string,
  }),
  onCampaignTagClick: PropTypes.func,
  hasCampaignsFeature: PropTypes.bool,
  postContent: PropTypes.shape({}),
  annotations: PropTypes.arrayOf(PropTypes.shape({})),
};

Post.defaultProps = {
  isConfirmingDelete: false,
  isDeleting: false,
  isWorking: false,
  isManager: true,
  day: null,
  dueTime: null,
  sharedBy: null,
  basic: false,
  shouldShowEditButton: true,
  hasAnalyticsOnPosts: false,
  hasTwitterImpressions: false,
  channelData: null,
  isCampaign: false,
  draggable: false,
  dragging: false,
  isOver: false,
  serviceLink: null,
  onDeleteConfirmClick: null,
  onDropPost: null,
  onEditClick: null,
  onShareNowClick: null,
  onShareAgainClick: null,
  onShareLinkClick: null,
  onRequeueClick: null,
  locationName: null,
  sourceUrl: null,
  subprofileID: null,
  subprofiles: null,
  statistics: null,
  profileService: null,
  profileServiceType: null,
  commentEnabled: false,
  commentText: null,
  hasCommentEnabled: false,
  hasFirstCommentFlip: false,
  hasPushNotifications: false,
  onSetRemindersClick: () => {},
  hasUserTags: false,
  campaignDetails: null,
  onCampaignTagClick: () => {},
  hasCampaignsFeature: false,
  postContent: null,
  annotations: null,
};

export default Post;
